// apps/web/src/routes/TenantApp.tsx
//
// The tenant-scoped platform shell. Every spec UI route lives under
// /app/:tenantSlug/* (reference/urls.md). This component:
//   - shows a group sidebar + tenant context (slug in the header)
//   - maps the first splat segment to the matching manager component
//   - renders a generic detail page for deeper unknown sub-paths so the
//     full 896-route surface resolves to useful UI at runtime
//
// The group → component lookup table drives the sidebar AND the splat
// routing, so adding a group is one line in the table.

import { Link, useParams, useLocation } from 'react-router-dom';
import { CompleteDashboard } from '../components/dashboard/CompleteDashboard.js';
import { BrandManager } from '../components/brands/BrandManager.js';
import { BrandSafetyDashboard } from '../components/brand-safety/BrandSafetyDashboard.js';
import { CampaignAnalytics } from '../components/campaigns/CampaignAnalytics.js';
import { ContentLibrary } from '../components/content/ContentLibrary.js';
import { CreatorManagement } from '../components/creators/CreatorManagement.js';
import { CreatorDiscovery } from '../components/creators/CreatorDiscovery.js';
import { DeliverableManager } from '../components/deliverables/DeliverableManager.js';
import { DisputesManager } from '../components/disputes/DisputesManager.js';
import { DocumentsManager } from '../components/documents/DocumentsManager.js';
import { FileManager } from '../components/files/FileManager.js';
import { FinanceDashboard } from '../components/finance/FinanceDashboard.js';
import { InvoiceManager } from '../components/finance/InvoiceManager.js';
import { HelpCenter } from '../components/help/HelpCenter.js';
import { IntegrationManager } from '../components/integrations/IntegrationManager.js';
import { IssuesManager } from '../components/issues/IssuesManager.js';
import { ProgrammeManager } from '../components/programmes/ProgrammeManager.js';
import { ReportsManager } from '../components/reports/ReportsManager.js';
import { RightsDashboard } from '../components/rights/RightsDashboard.js';
import { SecurityManager } from '../components/security/SecurityManager.js';
import { TeamManager } from '../components/teams/TeamManager.js';
import { WorkspaceManager } from '../components/workspaces/WorkspaceManager.js';
import { ToolsManager } from '../components/tools/ToolsManager.js';
import { AgreementsManager } from '../components/agreements/AgreementsManager.js';
import { OffersManager } from '../components/offers/OffersManager.js';
import { OrganisationManager } from '../components/organisations/OrganisationManager.js';
import { PartyManager } from '../components/parties/PartyManager.js';
import { SettlementManager } from '../components/settlements/SettlementManager.js';
import { PaymentsManager } from '../components/payments/PaymentsManager.js';
import { SocialAccountsManager } from '../components/social-accounts/SocialAccountsManager.js';
import { WebhooksManager } from '../components/webhooks/WebhooksManager.js';
import { PaymentAccountsManager } from '../components/payment-accounts/PaymentAccountsManager.js';
import { ApiKeysManager } from '../components/api-keys/ApiKeysManager.js';
import { SubscriptionsManager } from '../components/subscriptions/SubscriptionsManager.js';
import { TaxManager } from '../components/tax/TaxManager.js';
import { NotificationsManager } from '../components/notifications/NotificationsManager.js';
import { AuditLogsViewer } from '../components/audit-logs/AuditLogsViewer.js';
import { ConsentsManager } from '../components/consents/ConsentsManager.js';
import { ApiDocs } from '../components/docs/ApiDocs.js';
import { BackupManager } from '../components/backup/BackupManager.js';
import { MonitoringDashboard } from '../components/monitoring/MonitoringDashboard.js';
import { SearchResults } from '../components/search/SearchResults.js';
import { BreachDetection } from '../components/breach/BreachDetection.js';
import { MobileModule } from '../components/mobile/MobileModule.js';
import { ProfileModule } from '../components/profile/ProfileModule.js';
import { UsersModule } from '../components/users/UsersModule.js';
import { StatusModule } from '../components/status/StatusModule.js';
import { ResourceDetail, RESOURCE_PROFILES } from '../components/platform/ResourceDetail.js';
import type { ReactNode } from 'react';

interface GroupEntry {
  key: string;
  label: string;
  element: ReactNode;
}

// The group table. Sidebar + splat routing both read from this.
const GROUPS: GroupEntry[] = [
  { key: '', label: 'Dashboard', element: <CompleteDashboard /> },
  { key: 'campaigns', label: 'Campaigns', element: <CampaignAnalytics campaignId="current" /> },
  { key: 'creators', label: 'Creators', element: <CreatorManagement tenantId="current" /> },
  { key: 'creators/discovery', label: 'Creator Discovery', element: <CreatorDiscovery /> },
  { key: 'content', label: 'Content', element: <ContentLibrary /> },
  { key: 'deliverables', label: 'Deliverables', element: <DeliverableManager campaignId="current" /> },
  { key: 'rights', label: 'Rights', element: <RightsDashboard /> },
  { key: 'brands', label: 'Brands', element: <BrandManager /> },
  { key: 'brand-safety', label: 'Brand Safety', element: <BrandSafetyDashboard /> },
  { key: 'finance', label: 'Finance', element: <FinanceDashboard /> },
  { key: 'invoices', label: 'Invoices', element: <InvoiceManager /> },
  { key: 'payments', label: 'Payments', element: <PaymentsManager /> },
  { key: 'settlements', label: 'Settlements', element: <SettlementManager /> },
  { key: 'agreements', label: 'Agreements', element: <AgreementsManager /> },
  { key: 'offers', label: 'Offers', element: <OffersManager /> },
  { key: 'organisations', label: 'Organisations', element: <OrganisationManager /> },
  { key: 'parties', label: 'Parties', element: <PartyManager /> },
  { key: 'social-accounts', label: 'Social Accounts', element: <SocialAccountsManager /> },
  { key: 'payment-accounts', label: 'Payment Accounts', element: <PaymentAccountsManager /> },
  { key: 'programmes', label: 'Programmes', element: <ProgrammeManager /> },
  { key: 'teams', label: 'Teams', element: <TeamManager /> },
  { key: 'workspaces', label: 'Workspaces', element: <WorkspaceManager /> },
  { key: 'integrations', label: 'Integrations', element: <IntegrationManager /> },
  { key: 'issues', label: 'Issues', element: <IssuesManager /> },
  { key: 'disputes', label: 'Disputes', element: <DisputesManager /> },
  { key: 'documents', label: 'Documents', element: <DocumentsManager /> },
  { key: 'files', label: 'Files', element: <FileManager /> },
  { key: 'reports', label: 'Reports', element: <ReportsManager /> },
  { key: 'api-keys', label: 'API Keys', element: <ApiKeysManager /> },
  { key: 'webhooks', label: 'Webhooks', element: <WebhooksManager /> },
  { key: 'subscriptions', label: 'Subscriptions', element: <SubscriptionsManager /> },
  { key: 'audit-logs', label: 'Audit Logs', element: <AuditLogsViewer /> },
  { key: 'notifications', label: 'Notifications', element: <NotificationsManager /> },
  { key: 'consents', label: 'Consents', element: <ConsentsManager /> },
  { key: 'privacy', label: 'Privacy', element: <ConsentsManager /> },
  { key: 'security', label: 'Security', element: <SecurityManager /> },
  { key: 'breach-detection', label: 'Breach Detection', element: <BreachDetection /> },
  { key: 'tax', label: 'Tax', element: <TaxManager /> },
  { key: 'monitoring', label: 'Monitoring', element: <MonitoringDashboard /> },
  { key: 'backup', label: 'Backup', element: <BackupManager /> },
  { key: 'docs', label: 'Developer Docs', element: <ApiDocs /> },
  { key: 'tools', label: 'Tools', element: <ToolsManager /> },
  { key: 'help', label: 'Help', element: <HelpCenter /> },
  { key: 'profile', label: 'Profile', element: <ProfileModule /> },
  { key: 'users', label: 'Users', element: <UsersModule /> },
  { key: 'mobile', label: 'Mobile', element: <MobileModule /> },
  { key: 'search', label: 'Search', element: <SearchResults /> },
  { key: 'status', label: 'Status', element: <StatusModule /> },
  { key: 'tenants', label: 'Tenants', element: <WorkspaceManager /> },
];

function matchGroup(splat: string): GroupEntry | undefined {
  const first = splat.split('/').filter(Boolean)[0] ?? '';
  if (!first) return GROUPS[0];
  return GROUPS.find((g) => g.key && first.startsWith(g.key.split('/')[0]!));
}

function GenericDetail({ splat }: { splat: string }): JSX.Element {
  const parts = splat.split('/').filter(Boolean);
  const group = parts[0];
  const id = parts[1] ?? '';
  return (
    <div className="generic-detail">
      <h2 className="capitalize">{group ?? 'Resource'}</h2>
      <p className="muted">Detail view for {id || 'this resource'}.</p>
      <div className="manager-card">
        <h3>Summary</h3>
        <p>Resource path: /app/{'{tenant}'}/{splat}</p>
        <div className="actions">
          <button type="button" className="btn btn-secondary">Edit</button>
          <button type="button" className="btn btn-warning">History</button>
          <button type="button" className="btn btn-danger">Delete</button>
        </div>
      </div>
    </div>
  );
}

export function TenantApp(): JSX.Element {
  const { tenantSlug = 'workspace', '*': splat = '' } = useParams();
  const group = matchGroup(splat);
  const segments = splat.split('/').filter(Boolean).map((seg) => decodeURIComponent(seg));

  return (
    <div className="tenant-app">
      <aside className="tenant-sidebar">
        <Link to={`/app/${tenantSlug}`} className="tenant-brand">
          {tenantSlug}
        </Link>
        <nav className="tenant-nav">
          {GROUPS.map((g) => {
            const to = `/app/${tenantSlug}${g.key ? `/${g.key}` : ''}`;
            const active = splat.startsWith(g.key);
            return (
              <Link key={g.key || 'home'} to={to} className={active ? 'active' : ''}>
                {g.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      <main className="tenant-main">
        <header className="tenant-header">
          <nav className="breadcrumbs" aria-label="breadcrumbs">
            <Link to="/app">App</Link>
            <span>/</span>
            <Link to={`/app/${tenantSlug}`}>{tenantSlug}</Link>
            {segments.length > 0 && (
              <>
                <span>/</span>
                <span className="current">{segments[0]}</span>
              </>
            )}
          </nav>
        </header>

        <div className="tenant-content">
          {segments[0] === 'search' ? (
            <SearchResults />
          ) : segments.length >= 2 && RESOURCE_PROFILES[segments[0]!] ? (
            // Deep resource path: /app/{slug}/{group}/{id}[/...] →
            // real API-backed detail + edit page.
            <ResourceDetail group={segments[0]!} id={segments[1]!} />
          ) : group && segments.length <= 2 ? (
            group.element
          ) : group?.key && segments.length > 2 ? (
            <GenericDetail splat={splat} />
          ) : group ? (
            group.element
          ) : (
            <GenericDetail splat={splat} />
          )}
        </div>
      </main>
    </div>
  );
}

// Flat /app/* redirect target: show the same shell under the default
// "workspace" slug when no slug is present.
export function FlatAppRedirect(): JSX.Element {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/app\/?/, '');
  const target = `/app/workspace${rest ? `/${rest}` : ''}${location.search}`;
  return <Link to={target}>Open your workspace →</Link>;
}